import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useLocation, useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { confAxios } from '../../config/api';

const SIGNUP_WEB_URL = `${window.location.origin}/signup`;

const ua = () => navigator.userAgent || '';
const isAndroid = () => /Android/i.test(ua());
const isIOS = () => /(iPhone|iPad|iPod)/i.test(ua());
const isDesktop = () => !isAndroid() && !isIOS();
const isAndroidPhone = () => isAndroid() && /Mobile/i.test(ua());
const isIPhone = () => /(iPhone|iPod)/i.test(ua());
const isMobilePhone = () => isAndroidPhone() || isIPhone(); 
const isChromeOnAndroid = () => isAndroid() && /Chrome\/\d+/.test(ua());

function classifyError(raw) {
  const s = (raw || '').toLowerCase();
  if (s.includes("n'existe") || s.includes('existe') || s.includes('exist')) return 'not_found';
  if (s.includes('expir')) return 'expired';
  if (s.includes('invalid') || s.includes('invalide')) return 'invalid';
  return 'unknown';
}
const errorKey = (kind) => ({
  not_found: 'placeholder.tokenNotFound',
  expired:   'placeholder.tokenExpired',
  invalid:   'placeholder.tokenInvalid',
}[kind] || 'placeholder.emailError');

export default function VerifyEmail() {
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation();

  const handleSuccessDesktop = () => {
    history.push('/login', { banner: { type: 'success', message: t('placeholder.emailVerified') } });
  };

  const handleErrorDesktop = (key) => {
    history.push('/signup', { banner: { type: 'error', message: t(key) } });
  };

  const handleSuccessMobile = () => {
    const msg = t('placeholder.emailVerified');
    history.push('/mobile', {
      verified: true,
      reasonCode: 'success',
      message: msg,
    });
  };

  const handleErrorMobile = (kind) => {
    const key = errorKey(kind);
    history.push('/mobile', {
      verified: false,
      reasonCode: kind,
      message: t(key),
    });
  };

  useEffect(() => {
    const qp = new URLSearchParams(location.search);
    const token = qp.get('token');

    if (!token) {
      const key = 'placeholder.tokenMissing';
      if (isDesktop()) {
        handleErrorDesktop(key);
      } else if (isMobilePhone()) {
        history.replace('/mobile', {
          verified: false,
          reasonCode: 'missing',
          message: t(key),
        });
      } else {
        setStatus('error');
        setMessage(t(key));
      }
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${confAxios.baseURL}/users/activate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ token })
        });

        if (res.ok) {
          if (isDesktop()) {
            handleSuccessDesktop();
          } else if (isMobilePhone()) {
            handleSuccessMobile();
          } else {
            setStatus('success');
            setMessage(t('placeholder.emailVerifiedMobile'));
          }
          return;
        }

        let serverMsg = '';
        try {
          const data = await res.json();
          serverMsg = data?.statusLabel || '';
        } catch {
          try { serverMsg = await res.text(); } catch {}
        }
        const kind = classifyError(serverMsg);

        if (isDesktop()) {
          handleErrorDesktop(errorKey(kind));
        } else if (isMobilePhone()) {
          handleErrorMobile(kind);
        } else {
          setStatus('error');
          setMessage(t(errorKey(kind)));
        }
      } catch {
        if (isDesktop()) {
          handleErrorDesktop('placeholder.emailError');
        } else if (isMobilePhone()) {
          handleErrorMobile('unknown');
        } else {
          setStatus('error');
          setMessage(t('placeholder.emailError'));
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
      <Helmet><title>GrayFox - Email Verification</title></Helmet>
      {status === 'verifying' && <p className="text-gray-600">{t('placeholder.verifying')}</p>}
      {status === 'success' && <p className="text-green-700 font-medium text-center">{message}</p>}
      {status === 'error' && <p className="text-red-700 font-medium text-center">{message}</p>}
    </div>
  );
}
